// stores/assistantHealth.js — whether the assistant this build names answers.
//
// The panel's assistant is the plugin served at `VITE_ASSISTANT` (`config.js`). Three things can be true
// of it, and the store holds the one that is:
//
//   unconfigured  the build names none: there is no chat and no way to ask for one.
//   connected     the plugin answers `GET /health` with `registered: true`: the harness holds its profile.
//   unavailable   the plugin does not answer, or answers `registered: false`.
//
// `checking` is the moment before the first answer. The asking is the design system's
// (`assistantHealthOf`: on watch, then on a cadence and whenever the tab comes back into view); this
// store adds the one standing that is the panel's own, `unconfigured`.

import { createStore } from "@thekrystalship/krystal/lib/store";
import { assistantHealthOf } from "@thekrystalship/krystal-chat/lib/assistantHealth";
import { assistant } from "../assistantClient.js";
import { assistantSession } from "../assistantSession.js";

// The identity the chat addresses: the one assistant of the panel, under a fixed id because the chat and
// its stored conversations key on it.
const ASSISTANT_HOST = { id: "assistant", name: "Assistant", title: "the assistant" };

const assistantStore = createStore({ state: assistantSession.hasRoute() ? "checking" : "unconfigured" });

function set(state) {
  if (assistantStore.getState().state !== state) assistantStore.setState({ state });
}

const healthOf = () => assistantHealthOf(assistant.client());

let release = null;
let unsubscribe = null;

// Ask now.
async function probe() {
  if (!assistantSession.hasRoute()) { set("unconfigured"); return; }
  const health = healthOf();
  await health.probe();
  set(health.getState().state);
}

function start() {
  if (release || !assistantSession.hasRoute()) return;
  const health = healthOf();
  unsubscribe = health.subscribe(() => set(health.getState().state));
  release = health.watch();
  set(health.getState().state);
}

function stop() {
  if (release) { release(); release = null; }
  if (unsubscribe) { unsubscribe(); unsubscribe = null; }
  set(assistantSession.hasRoute() ? "checking" : "unconfigured");
}

assistantStore.probe = probe;
assistantStore.start = start;
assistantStore.stop = stop;

export { ASSISTANT_HOST, assistantStore };
